import React from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Radio,
  RadioGroup,
  Typography,
} from '@material-ui/core';
import { getCottageName } from '../utils';

const DELETE_FROM_LISTING = 'fromListing';
const DELETE_FROM_ALL = 'fromAll';

const DeleteDialog = ({
  open,
  file,
  cottages,
  isUpdating,
  onDeleteAll,
  onRemoveFromListing,
  onClose,
}) => {
  const isShared = file && (file.listing_ids || []).length >= 2;
  const [choice, setChoice] = React.useState(DELETE_FROM_LISTING);

  React.useEffect(() => {
    if (open) setChoice(DELETE_FROM_LISTING);
  }, [open]);

  const listingNames = React.useMemo(() => {
    if (!file || !isShared) return [];
    return (file.listing_ids || []).map((lid) => {
      const c = (cottages || []).find((ct) => ct.id === lid);
      return getCottageName(c) || `#${lid}`;
    });
  }, [file, isShared, cottages]);

  const handleConfirm = () => {
    if (!file) return;
    if (isShared && choice === DELETE_FROM_LISTING) {
      onRemoveFromListing();
    } else {
      onDeleteAll();
    }
  };

  const handleClose = (event, reason) => {
    if (isUpdating) return;
    onClose(event, reason);
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      disableEscapeKeyDown={isUpdating}
      maxWidth="xs"
      fullWidth
    >
      <DialogTitle>
        <FormattedMessage id="hostFiles.deleteTitle" defaultMessage="Delete file" />
      </DialogTitle>
      <DialogContent>
        {isShared ? (
          <>
            <Typography gutterBottom>
              <FormattedMessage
                id="hostFiles.confirmDeleteShared"
                defaultMessage="This file is used in multiple listings."
              />
            </Typography>
            <Typography variant="body2" color="textSecondary" gutterBottom>
              {listingNames.length <= 2 ? (
                listingNames.join(', ')
              ) : (
                <FormattedMessage
                  id="hostFiles.listingNamesTruncated"
                  defaultMessage="{shown} and {remaining} more"
                  values={{
                    shown: listingNames.slice(0, 2).join(', '),
                    remaining: listingNames.length - 2,
                  }}
                />
              )}
            </Typography>
            <RadioGroup value={choice} onChange={(e) => setChoice(e.target.value)}>
              <FormControlLabel
                value={DELETE_FROM_LISTING}
                control={<Radio />}
                label={
                  <FormattedMessage
                    id="hostFiles.removeFromThisListing"
                    defaultMessage="Remove from this listing"
                  />
                }
              />
              <FormControlLabel
                value={DELETE_FROM_ALL}
                control={<Radio />}
                label={
                  <FormattedMessage
                    id="hostFiles.deleteFromAll"
                    defaultMessage="Remove from all listings"
                  />
                }
              />
            </RadioGroup>
            {choice === DELETE_FROM_ALL && (
              <Typography variant="body2" color="textSecondary" style={{ marginLeft: 32 }}>
                <FormattedMessage
                  id="hostFiles.deletePermanent"
                  defaultMessage="The file will be permanently deleted from your account"
                />
              </Typography>
            )}
          </>
        ) : (
          <Typography color="textSecondary">
            <FormattedMessage
              id="hostFiles.deletePermanent"
              defaultMessage="The file will be permanently deleted from your account"
            />
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isUpdating} variant="outlined">
          <FormattedMessage id="Cancel" defaultMessage="Cancel" />
        </Button>
        <Button onClick={handleConfirm} color="secondary" disabled={isUpdating} variant="contained">
          <FormattedMessage id="hostFiles.deleteConfirm" defaultMessage="Delete" />
        </Button>
      </DialogActions>
    </Dialog>
  );
};

DeleteDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  file: PropTypes.shape({
    listing_ids: PropTypes.arrayOf(PropTypes.number),
  }),
  cottages: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      title_sv: PropTypes.string,
      title: PropTypes.string,
    }),
  ),
  isUpdating: PropTypes.bool,
  onDeleteAll: PropTypes.func.isRequired,
  onRemoveFromListing: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default DeleteDialog;
